<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="640px"
    append-to-body
    @open="onOpen"
    @close="onClose">
    <div v-loading="loading"
         class="asr-bound-substep-dialog">
      <div class="asr-bound-substep-dialog__section">
        <div class="asr-bound-substep-dialog__section-title">已关联分步模板</div>
        <div v-if="!loading && list.length === 0"
             class="asr-bound-substep-dialog__empty">
          暂无关联
        </div>
        <ul v-else
            class="asr-bound-substep-dialog__list">
          <li v-for="item in list"
              :key="item.id"
              class="asr-bound-substep-dialog__item">
            <router-link :to="stepTemplatePath(item)"
                         class="asr-bound-substep-dialog__link"
                         target="_blank">
              <el-link type="primary">{{ formatTitleWithId(item) }}</el-link>
            </router-link>
            <el-button type="text"
                       class="asr-bound-substep-dialog__unbind"
                       icon="el-icon-delete"
                       title="去掉关联"
                       :loading="unbindingId === item.id"
                       @click="onUnbind(item)" />
          </li>
        </ul>
      </div>

      <div class="asr-bound-substep-dialog__section">
        <div class="asr-bound-substep-dialog__section-title">添加新关联</div>
        <div class="asr-bound-substep-dialog__add">
          <remote-select
            v-model="addSubstepId"
            class="asr-bound-substep-dialog__select"
            clearable
            keyword-key="keyword"
            label-key="title"
            value-key="id"
            id-key="id"
            placeholder="请输入分步模板关键字"
            :query-request="getStepTemplateTable"
            :find-request="getStepTemplateDetail" />
          <el-button type="primary"
                     size="small"
                     :loading="adding"
                     :disabled="!addSubstepId"
                     @click="onAdd">
            添加关联
          </el-button>
        </div>
        <div class="asr-bound-substep-dialog__tip">
          一个分步模板最多关联一个录音风险配置；若已关联其他配置，添加时会提示并换绑。
        </div>
      </div>
    </div>
    <span slot="footer">
      <el-button @click="dialogVisible = false">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'AudioAsrCheckBoundSubstepDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    templateId: {
      type: [Number, String],
      default: null,
    },
    templateTitle: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      loading: false,
      list: [],
      addSubstepId: null,
      adding: false,
      unbindingId: null,
      dirty: false,
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      },
    },
    dialogTitle () {
      const title = this.templateTitle || ''
      const id = this.templateId
      if (title && id) {
        return `管理关联分步模板（${title} / ${id}）`
      }
      return '管理关联分步模板'
    },
    boundIdSet () {
      const set = {}
      this.list.forEach((item) => {
        set[Number(item.id)] = true
      })
      return set
    },
  },
  methods: {
    ...request.map([
      'getStepTemplateTable',
      'getStepTemplateDetail',
    ]),
    onOpen () {
      this.dirty = false
      this.addSubstepId = null
      this.loadList()
    },
    onClose () {
      this.list = []
      this.addSubstepId = null
      this.adding = false
      this.unbindingId = null
      if (this.dirty) {
        this.$emit('changed')
        this.dirty = false
      }
    },
    loadList () {
      if (!this.templateId) {
        this.list = []
        return
      }
      this.loading = true
      request.common('listAudioAsrCheckBindByTemplate', { id: this.templateId }).then(({ data }) => {
        this.list = (data && data.data) ? data.data : []
      }).catch(() => {
        this.list = []
      }).finally(() => {
        this.loading = false
      })
    },
    formatTitleWithId (item) {
      const title = item.title == null || item.title === ''
        ? ''
        : String(item.title)
      const id = item.id == null || Number(item.id) === 0
        ? ''
        : String(item.id)
      if (title && id) {
        return `${title} / ${id}`
      }
      return title || id || '-'
    },
    stepTemplatePath (item) {
      const id = item.id
      if (Number(item.type) === 20) {
        return `/clb-step-template/update/${id}`
      }
      return `/step-template/update/${id}`
    },
    onUnbind (item) {
      const label = this.formatTitleWithId(item)
      this.$confirm(`确认去掉与「${label}」的关联吗？`, '提示', { type: 'warning' }).then(() => {
        this.unbindingId = item.id
        request.common('unbindAudioAsrCheckTemplate', item.id).then(() => {
          this.$message.success('已去掉关联')
          this.dirty = true
          this.loadList()
        }).finally(() => {
          this.unbindingId = null
        })
      }).catch(() => {})
    },
    onAdd () {
      const substepId = Number(this.addSubstepId)
      if (!substepId || !this.templateId) {
        return
      }
      if (this.boundIdSet[substepId]) {
        this.$message.warning('该分步模板已关联当前配置')
        return
      }

      this.adding = true
      request.common('getAudioAsrCheckBindBySubstep', { substepid: substepId }).then(({ data }) => {
        const boundTemplateId = data && data.audio_asr_check_template_id
          ? Number(data.audio_asr_check_template_id)
          : 0
        if (boundTemplateId > 0 && boundTemplateId !== Number(this.templateId)) {
          const boundTitle = data.template && data.template.title
            ? data.template.title
            : ''
          const boundLabel = boundTitle
            ? `${boundTitle} / ${boundTemplateId}`
            : String(boundTemplateId)
          return this.$confirm(
            `该分步模板已关联配置「${boundLabel}」，确认换绑到当前配置吗？`,
            '提示',
            { type: 'warning' }
          ).then(() => this.doBind(substepId))
        }
        return this.doBind(substepId)
      }).catch(() => {}).finally(() => {
        this.adding = false
      })
    },
    doBind (substepId) {
      return request.common('bindAudioAsrCheckTemplate', {
        substepid: substepId,
        audio_asr_check_template_id: this.templateId,
      }).then(() => {
        this.$message.success('关联成功')
        this.addSubstepId = null
        this.dirty = true
        this.loadList()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.asr-bound-substep-dialog {
  min-height: 120px;

  &__section {
    & + & {
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid #ebeef5;
    }
  }

  &__section-title {
    margin-bottom: 12px;
    color: #303133;
    font-size: 14px;
    font-weight: 500;
  }

  &__empty {
    color: #909399;
    font-size: 14px;
    text-align: center;
    padding: 16px 0;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 4px;
    border-bottom: 1px solid #ebeef5;

    &:last-child {
      border-bottom: none;
    }
  }

  &__link {
    flex: 1;
    min-width: 0;
    word-break: break-all;
  }

  &__unbind {
    flex-shrink: 0;
    padding: 0;
    font-size: 16px;
    color: #dc2626;

    &:hover {
      color: #b91c1c;
    }
  }

  &__add {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__select {
    flex: 1;
    min-width: 0;
  }

  &__tip {
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/audio-asr-check/components/AudioAsrCheckBoundSubstepDialog.vue